import React from "react";
import {
  Wrench,
  Network,
  Cctv,
  Globe,
  ServerCog,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";
import { motion } from "framer-motion";

const LAPTOP_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d63da504-59d9-46e2-b5e7-f0d7372ddd6f/images/00bfd33791bc502d1262ee97694d77c8791b632de3cfe5ae06044360accd8719.png";
const NET_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d63da504-59d9-46e2-b5e7-f0d7372ddd6f/images/74aa94d51fa4008c16d843b05d5e763e4aff6e4d95fbc062099a0a0a949a27ec.png";
const CCTV_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d63da504-59d9-46e2-b5e7-f0d7372ddd6f/images/d8f280d8e5c7de3cecb1ac2be480086be3e97dd9ef50b9f128853a329ad02567.png";

const items = [
  { icon: Wrench, label: "01 — REPARAȚII", title: "PC, laptop & periferice", desc: "Diagnoză profesională, schimbare componente, recuperare date și instalare Windows / Linux. Devirusare și optimizare în aceeași zi.", image: LAPTOP_IMG, span: "lg:col-span-8 lg:row-span-2", dark: true, big: true },
  { icon: Network, label: "02 — REȚELE", title: "WiFi & cablare structurată", desc: "Routere, switch-uri, mesh-uri WiFi 6 pentru casă și birou.", image: NET_IMG, span: "lg:col-span-4", light: true },
  { icon: Cctv, label: "03 — SUPRAVEGHERE", title: "Camere CCTV & alarme", desc: "Sisteme IP cu acces de pe telefon, instalare și mentenanță.", image: CCTV_IMG, span: "lg:col-span-4", warm: true },
  { icon: Globe, label: "04 — WEB", title: "Site-uri & găzduire", desc: "Site de prezentare, magazin online, hosting cu monitorizare.", span: "lg:col-span-4", light: true },
  { icon: ServerCog, label: "05 — ABONAMENT", title: "Suport IT pentru firme", desc: "Intervenție rapidă on-site & remote, monitorizare proactivă.", span: "lg:col-span-5", light: true },
  { icon: ShieldCheck, label: "06 — CLOUD", title: "Backup & Email", desc: "Google Workspace, M365, backup automat zilnic.", span: "lg:col-span-3", dark: true },
];

export default function ServicesBento() {
  return (
    <section id="servicii" data-testid="services-section" className="py-24 lg:py-36 border-t border-line">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="overline text-clay">SERVICIILE NOASTRE</span>
            <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] max-w-2xl">
              De la un cablu desfăcut la o infrastructură completă.
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-inksoft max-w-md"
          >
            Tot ce înseamnă „IT" într-o singură echipă. Dacă merge pe
            electricitate și are LED-uri, ne ocupăm.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5 auto-rows-[260px]">
          {items.map((it, i) => (
            <BentoCard key={i} item={it} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BentoCard({ item, index }) {
  const bg = item.dark ? "bg-ink text-sand" : item.warm ? "bg-sandwarm text-ink" : "bg-white text-ink";
  const border = item.dark ? "border-ink" : "border-line";
  const rowSpan = item.big ? "row-span-2" : "";

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: index * 0.06, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`service-card-${index}`}
      className={`group relative rounded-2xl border p-7 lg:p-8 overflow-hidden ${bg} ${border} ${item.span} ${rowSpan} flex flex-col justify-between hover:-translate-y-1 hover:shadow-[0_28px_50px_-32px_rgba(26,27,26,0.4)] transition-all duration-500`}
    >
      <div className="flex items-center gap-3 relative z-10">
        <span className={`w-9 h-9 grid place-items-center rounded-lg border ${item.dark ? "border-sand/15 bg-sand/5" : "border-ink/10 bg-ink/5"}`}>
          <item.icon size={18} className="text-clay" />
        </span>
        <span className="overline opacity-70">{item.label}</span>
      </div>

      <div className="relative z-10 mt-8">
        <h3 className={`font-display ${item.big ? "text-3xl lg:text-4xl" : "text-2xl"} font-bold tracking-[-0.03em] max-w-[20ch] leading-[1.05]`}>
          {item.title}
        </h3>
        {item.desc && (
          <p className={`mt-3 text-sm leading-relaxed max-w-md ${item.dark ? "text-sand/75" : "text-inksoft"}`}>
            {item.desc}
          </p>
        )}
        {item.big && (
          <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold">
            Aflați mai mult
            <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </div>
        )}
      </div>

      {item.image && item.big && (
        <img
          src={item.image}
          alt=""
          className="absolute -bottom-14 -right-10 w-[55%] opacity-80 mix-blend-screen pointer-events-none"
        />
      )}
      {item.image && !item.big && (
        <img
          src={item.image}
          alt=""
          className="absolute inset-x-6 bottom-4 h-24 object-cover rounded-lg opacity-90"
        />
      )}
    </motion.article>
  );
}
